import {
    LoyaltyCurrency,
    LoyaltyCurrencyType,
    LoyaltyMember,
    LoyaltyPendingPoints,
    LoyaltyProgram,
    LoyaltyTierInformation,
    LoyaltyTierLevel } from '@rdss/client'

// eslint-disable-next-line new-cap
const formatter = Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
})

// eslint-disable-next-line new-cap
const currencyFormatter = Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
})

/**
 * Get the current loyalty tier of a loyalty member.
 */
export const getLoyaltyTier = (loyaltyMember: LoyaltyMember): LoyaltyTierLevel => {
    return loyaltyMember?.tier?.name ?? LoyaltyTierLevel.basic
}

/**
 * Get the current bonus points of a loyalty member.
 *
 */
export const getNonQualifyingPoints = (loyaltyMember: LoyaltyMember): number => {
    if (loyaltyMember?.currencies.length > 0) {
        return loyaltyMember.currencies.find(
            (el: LoyaltyCurrency) =>
                el.type === LoyaltyCurrencyType.nonQualifying
        )?.pointsBalance ?? 0;
    }

    return 0;
}

/**
 * Get the sum of all pending points of a loyalty member.
 */
export const getEscrowPoints = (loyaltyMember: LoyaltyMember): number => {
    if (loyaltyMember?.currencies.length > 0) {
        return loyaltyMember.currencies.find(
            (el: LoyaltyCurrency) =>
                el.type === LoyaltyCurrencyType.nonQualifying
        )?.escrowPointsBalance ?? 0;
    }

    return -1;
}

/**
 * Get the pending points of a loyalty member.
 */
export const getPendingPoints = (loyaltyMember: LoyaltyMember): LoyaltyPendingPoints[] => {
    return loyaltyMember.pendingPoints.map((pendingPoint) => {
        return {
            points: pendingPoint.points,
            availabilityDate: formatter.format(new Date(pendingPoint.availabilityDate)),
        }
    })
}

/**
 * Get the multiplying factor of a specific tier.
 */
export const getTierMultiplyingFactor = (loyaltyTiers: LoyaltyTierInformation[], tier: LoyaltyTierLevel): number => {
    const loyaltyInformation = loyaltyTiers?.find(tierInfo => tierInfo.name === tier)
    return loyaltyInformation?.pointsMultiplyingFactor ?? 2
}

/**
 * Get the qualifying points of a loyalty member.
 */
export const getQualifyingPoints = (loyaltyMember: LoyaltyMember): number => {
    if (loyaltyMember?.currencies.length > 0) {
        return loyaltyMember.currencies.find(
            (el: LoyaltyCurrency) =>
                el.type === LoyaltyCurrencyType.qualifying
        )?.pointsBalance ?? 0
    }

    return 0
}

/**
 * Get the qualifying expiration date of a loyalty member.
 */
export const getQualifyingExpirationDate = (loyaltyMember: LoyaltyMember): string => {
    if (loyaltyMember?.currencies.length > 0) {
        return loyaltyMember.currencies.find(
            (el: LoyaltyCurrency) =>
                el.type === LoyaltyCurrencyType.qualifying
        )?.nextResetDate ?? ''
    }

    return ''
}

/**
 * Get the qualifying expiration date of a loyalty member in the format "dd.mm.yyyy".
 */
export const getQualifyingExpirationDateFormatted = (loyaltyMember: LoyaltyMember): string => {
    if (loyaltyMember?.currencies.length > 0) {
        return formatter.format(new Date(loyaltyMember.currencies.find(
            (el: LoyaltyCurrency) =>
                el.type === LoyaltyCurrencyType.qualifying
        )?.nextResetDate)) ?? ''
    }

    return ''
}

/**
 * Get the cost per unit of a loyalty program or the factor to burn points.
 */
export const getBurnFactor = (loyaltyProgram: LoyaltyProgram): number => {
    return loyaltyProgram?.costPerUnit ?? 0
}

/**
 * Get the points to earn for a specific price.
 */
export const getPointsToEarn = (price?: number, pointEarnFactor?: number): number => {
    return price && pointEarnFactor ? Math.round(price) * pointEarnFactor : 0
}

/**
 * Get the points to burn.
 */
export const getMoneyToBurn = (pointsToBurn: number, loyaltyProgram: LoyaltyProgram) => {
    return currencyFormatter.format(pointsToBurn * getBurnFactor(loyaltyProgram) ?? 0)
}

/**
 * Calculate the points to burn.
 */
export const calculateMoneyToBurn = (pointsToBurn: number, costPerUnit: number) => {
    return currencyFormatter.format(pointsToBurn * costPerUnit ?? 0)
}
